import { showApproveRequestState, showViewApprovalRequestState } from '@/atoms';
import { Button, Badge } from '@mantine/core';
import { useRecoilState } from 'recoil-next';
import useAuth from '@/hooks/useAuth';
import { UserRole } from '@/types';
import { EApprovalPriority } from '@/types/v2';
import { ModalLayout } from 'moh-ui';

export default function ViewApprovalRequest() {
   const [show, setShow] = useRecoilState(showViewApprovalRequestState);
   const [, setShowApproveRequest] = useRecoilState(showApproveRequestState);
   const { user } = useAuth();

   const formatDate = (dateString: string) => {
      if (!dateString) return 'N/A';
      return new Date(dateString).toLocaleString();
   };

   const renderStatusBadge = (status: string) => {
      const statusClasses = {
         PENDING: 'bg-yellow-100 text-yellow-700',
         APPROVED: 'bg-green-100 text-green-700',
         REJECTED: 'bg-red-100 text-red-700',
      };

      return (
         <span
            className={`px-3 py-1 rounded-full text-sm font-semibold ${statusClasses[status as keyof typeof statusClasses] || 'bg-gray-100 text-gray-700'}`}
         >
            {status}
         </span>
      );
   };

   if (!show?.request) return null;

   const { request } = show;

   return (
      <ModalLayout open={Boolean(show?.show)} onClose={() => setShow(null)} size={800}>
         <div className="space-y-4">
            <div className="flex justify-between items-center">
               <h2 className="text-xl font-bold">Approval Request Details</h2>
               {renderStatusBadge(request.status)}
            </div>

            <hr className="my-4 border-gray-200" />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
               <div>
                  <span className="text-sm text-gray-500">Request ID</span>
                  <p className="text-gray-800">{request.id}</p>
               </div>
               <div>
                  <span className="text-sm text-gray-500">Case ID</span>
                  <p className="text-gray-800">{request.caseId || 'N/A'}</p>
               </div>
               <div>
                  <span className="text-sm text-gray-500">Created At</span>
                  <p className="text-gray-800">{formatDate(request.createdAt)}</p>
               </div>
               <div className="flex flex-col gap-1">
                  <span className="text-sm text-gray-500">Priority</span>
                  <Badge color={request.priority === EApprovalPriority.HIGH ? 'red' : 'blue'}>{request.priority}</Badge>
               </div>
            </div>

            <div className="border rounded-md p-4">
               <h4 className="font-bold text-lg mb-3">Patient Information</h4>
               <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                     <span className="text-sm text-gray-500">Name</span>
                     <p className="text-gray-800">
                        {request.patient.firstName} {request.patient.lastName}
                     </p>
                  </div>
                  <div>
                     <span className="text-sm text-gray-500">National ID</span>
                     <p className="text-gray-800">{request.patient.nationalId || 'N/A'}</p>
                  </div>
                  <div>
                     <span className="text-sm text-gray-500">Insurance Code</span>
                     <p className="text-gray-800">{request.patient.insuranceCode || 'N/A'}</p>
                  </div>
               </div>
            </div>

            {user?.roles[0].roleName !== UserRole.INSURANCE_ADMIN && (
               <div className="border rounded-md p-4">
                  <h4 className="font-bold text-lg mb-3">Insurance Information</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                     <div>
                        <span className="text-sm text-gray-500">Name</span>
                        <p className="text-gray-800">{request.insurance.name}</p>
                     </div>
                     <div>
                        <span className="text-sm text-gray-500">Insurance Key</span>
                        <p className="text-gray-800">{request.insurance.insuranceKey}</p>
                     </div>
                     <div className="col-span-1 md:col-span-2">
                        <span className="text-sm text-gray-500">Description</span>
                        <p className="text-gray-800">{request.insurance.description || 'N/A'}</p>
                     </div>
                  </div>
               </div>
            )}

            <div className="border rounded-md p-4">
               <h4 className="font-bold text-lg mb-3">Requested Item Details</h4>
               <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                     <span className="text-sm text-gray-500">Item Name</span>
                     <p className="text-gray-800">{request.requestedItemName}</p>
                  </div>
                  <div>
                     <span className="text-sm text-gray-500">Item Type</span>
                     <p className="text-gray-800">{request.requestedItemType}</p>
                  </div>
                  <div>
                     <span className="text-sm text-gray-500">Item Code</span>
                     <p className="text-gray-800">{request.requestedItemCode}</p>
                  </div>
                  <div>
                     <span className="text-sm text-gray-500">Medical Facility</span>
                     <p className="text-gray-800">{request.medicalFacility || 'N/A'}</p>
                  </div>
               </div>
            </div>

            {/* Dosage information if available */}
            {(request.dosagePeriod || request.dosageQuantity || request.dosageFrequency || request.dosageForm) && (
               <div className="border rounded-md p-4">
                  <h4 className="font-bold text-lg mb-3">Dosage Information</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                     <div>
                        <span className="text-sm text-gray-500">Dosage Period</span>
                        <p className="text-gray-800">{request.dosagePeriod || 'N/A'}</p>
                     </div>
                     <div>
                        <span className="text-sm text-gray-500">Dosage Quantity</span>
                        <p className="text-gray-800">{request.dosageQuantity || 'N/A'}</p>
                     </div>
                     <div>
                        <span className="text-sm text-gray-500">Dosage Frequency</span>
                        <p className="text-gray-800">{request.dosageFrequency || 'N/A'}</p>
                     </div>
                     <div>
                        <span className="text-sm text-gray-500">Dosage Form / UOM</span>
                        <p className="text-gray-800">{request.dosageForm || 'N/A'}</p>
                     </div>
                  </div>
               </div>
            )}

            <div className="border rounded-md p-4">
               <h4 className="font-bold text-lg mb-3">Additional Information</h4>
               <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="col-span-1 md:col-span-2">
                     <span className="text-sm text-gray-500">Message</span>
                     <p className="text-gray-800 whitespace-break-spaces">{request.message || 'N/A'}</p>
                  </div>

                  {request.status === 'REJECTED' && (
                     <div className="col-span-1 md:col-span-2">
                        <span className="text-sm text-gray-500">Rejection Reason</span>
                        <p className="text-gray-800">{request.rejectionReason || 'No reason provided'}</p>
                     </div>
                  )}

                  {(request.receptionistName || request.receptionistEmail) && (
                     <>
                        <div>
                           <span className="text-sm text-gray-500">Receptionist Name</span>
                           <p className="text-gray-800">{request.receptionistName || 'N/A'}</p>
                        </div>
                        <div>
                           <span className="text-sm text-gray-500">Receptionist Email</span>
                           <p className="text-gray-800">{request.receptionistEmail || 'N/A'}</p>
                        </div>
                     </>
                  )}
               </div>
            </div>
            {show.request.status === 'PENDING' && (
               <div className="flex gap-3 w-full border-t pt-4">
                  <Button
                     onClick={() => {
                        setShow(null);
                        setShowApproveRequest({ show: true, requestId: show.request.id, action: 'approve' });
                     }}
                     color="blue"
                     variant="outline"
                  >
                     Approve Request
                  </Button>
                  <Button
                     onClick={() => {
                        setShow(null);
                        setShowApproveRequest({ show: true, action: 'reject', requestId: show.request.id });
                     }}
                     color="red"
                     variant="outline"
                  >
                     Reject Request
                  </Button>
               </div>
            )}
         </div>
      </ModalLayout>
   );
}
